import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { IntlProvider } from 'react-intl'
import { catalogFor } from '../i18n/catalog'
import {
  SOURCE_LOCALE,
  DEFAULT_LOCALE,
  PSEUDO_LOCALE,
  pickerLocales,
  narrowToEnabled,
} from '../i18n/config'
import { useAuth } from './AuthContext'

const STORAGE_KEY = 'ip.locale'

interface LanguageContextValue {
  locale: string
  setLocale: (loc: string) => void
  availableLocales: string[]
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined)

export const useLanguage = (): LanguageContextValue => {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useLanguage must be used within a LanguageProvider')
  return ctx
}

// Resolution order (first match wins), applied synchronously at first render so
// pre-auth screens paint in the right language with no flash:
//   1. localStorage['ip.locale']  (device-local pre-auth choice)
//   2. navigator.language, but only when it asks for an enabled language other
//      than English
//   3. DEFAULT_LOCALE ('fr')
// English is the one browser language the default overrides: an English default
// is what sent francophone visitors to the wrong page, while a Spanish speaker
// is not served better by French. The authenticated user's saved locale takes
// over once AuthContext resolves, via <UserLocaleSync/> below.
export function resolveInitialLocale(): string {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored && pickerLocales().includes(stored)) return stored
  } catch {
    /* localStorage unavailable (SSR / privacy mode) */
  }
  const nav = typeof navigator !== 'undefined' ? narrowToEnabled(navigator.language) : null
  if (nav && nav !== SOURCE_LOCALE) return nav
  return DEFAULT_LOCALE
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<string>(resolveInitialLocale)

  const setLocale = (loc: string) => {
    setLocaleState(loc)
    try {
      window.localStorage.setItem(STORAGE_KEY, loc)
    } catch {
      /* ignore */
    }
  }

  // The document's own language follows the active locale: screen readers, the
  // browser's translation prompt and every crawler read this attribute.
  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = locale
  }, [locale])

  const messages = useMemo(() => catalogFor(locale), [locale])
  const availableLocales = useMemo(() => pickerLocales(), [])

  const value = useMemo<LanguageContextValue>(
    () => ({ locale, setLocale, availableLocales }),
    [locale, availableLocales],
  )

  return (
    <LanguageContext.Provider value={value}>
      <IntlProvider
        locale={locale}
        defaultLocale={SOURCE_LOCALE}
        messages={messages}
        // Do not crash on a missing key — the CI completeness check is the gate.
        // In dev the console warning still flags the gap; in prod we stay quiet.
        onError={(err) => {
          if (import.meta.env.DEV && err.code !== 'MISSING_TRANSLATION') {
            console.warn('[intl]', err.message)
          }
        }}
      >
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  )
}

// Bridges AuthContext -> LanguageContext. Rendered *inside* AuthProvider (which
// sits inside LanguageProvider), it upgrades the active locale to the signed-in
// user's saved preference on every device. Mounted once near the app root.
export const UserLocaleSync: React.FC = () => {
  const { user } = useAuth()
  const { locale, setLocale } = useLanguage()

  useEffect(() => {
    // The pseudo-locale is a testing override (the hardcoded-string sweep forces
    // it); it must win over the user's saved locale, so never override it here.
    if (locale === PSEUDO_LOCALE) return
    const userLocale = user?.locale
    if (userLocale && userLocale !== locale && pickerLocales().includes(userLocale)) {
      setLocale(userLocale)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.locale])

  return null
}
